// v1
// Path: frontend/src/components/SyncHub/shared/SetAccordionItem.jsx
// Inputs: set, isExpanded, onToggleExpand, token, expandedPresetId, onToggleExpandPreset, presets, availableTables, targetEntities, sourceSystem, targetSystem, onChangeSourceSystem, onChangeTargetSystem, onMakeDefaultSet, onDuplicateSet, onRenameSet, onDeleteSet, onAddNewPipeline, onSavePreset, onDuplicatePreset, onRenamePreset, onDeletePreset, onResetPresetDefaults
// Outputs: Tier 1 full-width expandable Set accordion row containing 3-column system alignment row, preset filter bar, Tier 2 PresetAccordionItem list, and new pipeline trigger.
// Summary: Component rendering the top-level Mapping Set accordion with summary statistics, system selection, and nested pipeline presets enforcing single-item expansion.

import React, { useState, useMemo } from 'react';
import SearchableSelect from '../SearchableSelect';
import PresetAccordionItem from './PresetAccordionItem';

export default function SetAccordionItem({
    set,
    isExpanded = false,
    onToggleExpand = () => {},
    token,
    expandedPresetId = null,
    onToggleExpandPreset = () => {},
    presets = [],
    availableTables = [],
    targetEntities = [],
    sourceSystem = 'Passport',
    targetSystem = 'QuickBooks',
    onChangeSourceSystem = () => {},
    onChangeTargetSystem = () => {},
    availableSourceSystems = ['Passport', 'QuickBooks', 'Xero'],
    availableTargetSystems = ['QuickBooks', 'Xero', 'Passport'],
    onMakeDefaultSet = () => {},
    onDuplicateSet = () => {},
    onRenameSet = () => {},
    onDeleteSet = () => {},
    onAddNewPipeline = () => {},
    onSavePreset = async () => {},
    onDuplicatePreset = () => {},
    onRenamePreset = () => {},
    onDeletePreset = () => {},
    onResetPresetDefaults = () => {}
}) {
    const setName = set.setName || set.name || set;
    const isDefault = !!set.isDefault;

    // Filter term for pipelines inside this set
    const [pipelineFilter, setPipelineFilter] = useState('');

    // Summary calculations across presets in this set
    const stats = useMemo(() => {
        const pipelineCount = presets.length;
        let mappedFieldsCount = 0;

        presets.forEach(p => {
            const raw = p.mappings || (p.FieldMappingsJson ? JSON.parse(p.FieldMappingsJson || '[]') : []);
            if (Array.isArray(raw)) {
                mappedFieldsCount += raw.filter(m => (m.sources && m.sources.length > 0) || (m.source && m.source.trim())).length;
            }
        });

        return { pipelineCount, mappedFieldsCount };
    }, [presets]);

    // Source & Target system options formatted for SearchableSelect
    const sourceSystemOptions = useMemo(() => {
        return (availableSourceSystems || ['Passport', 'QuickBooks', 'Xero']).map(s => ({
            value: s,
            label: s === 'Passport' ? `Passport ERP (${availableTables.length || 221} tables)` : s,
            description: s === 'Passport' ? 'Legacy ACUCOBOL ERP' : `${s} Accounting Platform`
        }));
    }, [availableSourceSystems, availableTables.length]);

    const targetSystemOptions = useMemo(() => {
        return (availableTargetSystems || ['QuickBooks', 'Xero', 'Passport']).map(t => ({
            value: t,
            label: t === 'QuickBooks' ? `QuickBooks Online (${targetEntities.length || 42} entities)` : t,
            description: t === 'QuickBooks' ? 'Intuit QuickBooks Online API' : `${t} Destination`
        }));
    }, [availableTargetSystems, targetEntities.length]);

    // Filter presets based on user search input
    const filteredPresets = useMemo(() => {
        if (!pipelineFilter.trim()) return presets;
        const q = pipelineFilter.toLowerCase().trim();
        return presets.filter(p => {
            const name = (p.MappingName || '').toLowerCase();
            const entity = (p.TargetQBEntity || p.EntityType || '').toLowerCase();
            const table = (p.SourceTable || '').toLowerCase();
            return name.includes(q) || entity.includes(q) || table.includes(q);
        });
    }, [presets, pipelineFilter]);

    return (
        <div className={`border rounded-xl transition-all duration-200 overflow-hidden ${
            isExpanded
                ? 'border-amber-500/50 bg-slate-900/90 shadow-xl'
                : 'border-slate-800 bg-slate-850/60 hover:border-slate-700'
        }`}>
            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* FULL-WIDTH TIER 1 SET ACCORDION HEADER                             */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <div
                onClick={onToggleExpand}
                className={`w-full flex items-center justify-between px-5 py-3.5 cursor-pointer select-none transition-colors ${
                    isExpanded ? 'bg-slate-850/90 border-b border-slate-750' : 'hover:bg-slate-800/60'
                }`}
            >
                {/* Left: Chevron, Set Name, Default Star Badge, and Metrics */}
                <div className="flex items-center gap-3 flex-wrap">
                    <span className="material-symbols-outlined text-slate-400 text-[20px] transition-transform duration-200">
                        {isExpanded ? 'expand_more' : 'chevron_right'}
                    </span>

                    <div className="flex items-center gap-2">
                        <span className="text-base font-bold text-white tracking-wide flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-[18px] text-amber-400">bookmark</span>
                            {setName}
                        </span>

                        {isDefault && (
                            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-amber-950/80 text-amber-300 border border-amber-800/80 flex items-center gap-1 shadow-sm">
                                <span className="material-symbols-outlined text-[13px] text-amber-400 fill-current">star</span>
                                Default Set
                            </span>
                        )}
                    </div>

                    {/* High-level summary badge: [X Pipelines • Y Mapped] */}
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-800 text-slate-300 border border-slate-700">
                        {stats.pipelineCount} {stats.pipelineCount === 1 ? 'Pipeline' : 'Pipelines'} &bull; {stats.mappedFieldsCount} Mapped
                    </span>
                </div>

                {/* Right: Action Controls (Make Default, Duplicate, Rename, Delete) */}
                <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
                    {!isDefault && (
                        <button
                            type="button"
                            onClick={() => onMakeDefaultSet(setName)}
                            className="px-2.5 py-1 bg-slate-800 hover:bg-amber-950/60 text-slate-300 hover:text-amber-300 border border-slate-700 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors"
                            title="Designate this set as the default set"
                        >
                            <span className="material-symbols-outlined text-[14px]">star_outline</span>
                            Make Default
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={() => onDuplicateSet(setName)}
                        className="px-2.5 py-1 bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white border border-slate-700 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors"
                        title="Duplicate this standard set"
                    >
                        <span className="material-symbols-outlined text-[14px]">content_copy</span>
                        Duplicate
                    </button>

                    <button
                        type="button"
                        onClick={() => onRenameSet(setName)}
                        className="px-2.5 py-1 bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white border border-slate-700 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors"
                        title="Rename this standard set"
                    >
                        <span className="material-symbols-outlined text-[14px]">edit</span>
                        Rename
                    </button>

                    <button
                        type="button"
                        onClick={() => onDeleteSet(setName)}
                        disabled={isDefault}
                        className={`px-2.5 py-1 border rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors ${
                            isDefault
                                ? 'bg-slate-800/40 text-slate-600 border-slate-800 cursor-not-allowed opacity-50'
                                : 'bg-slate-800 hover:bg-red-950/60 text-slate-400 hover:text-red-300 border-slate-700 hover:border-red-800/60'
                        }`}
                        title={isDefault ? "Cannot delete active default set. Make another set default first." : "Delete this set"}
                    >
                        <span className="material-symbols-outlined text-[14px]">delete</span>
                        Delete
                    </button>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* EXPANDED SET BODY                                                  */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            {isExpanded && (
                <div className="p-5 space-y-4 bg-slate-900/60">
                    
                    {/* ── 1. SYSTEM ALIGNMENT ROW (Strict 3 Columns: 1fr - 200px - 1fr) ── */}
                    <div className="p-4 rounded-xl bg-slate-850 border border-slate-750 shadow-sm">
                        <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-[15px] text-indigo-400">sync_alt</span>
                            System Pipeline Scope
                        </div>

                        <div className="grid grid-cols-[minmax(0,1fr)_200px_minmax(0,1fr)] gap-4 items-center">
                            {/* Col 1: Source System */}
                            <div className="w-full">
                                <SearchableSelect
                                    options={sourceSystemOptions}
                                    value={sourceSystem}
                                    onChange={onChangeSourceSystem}
                                    placeholder="Select source system..."
                                    className="w-full"
                                />
                            </div>

                            {/* Col 2: Progress / Entity Flow Badge */}
                            <div className="flex items-center justify-center gap-1.5 text-xs font-bold text-slate-400 select-none">
                                <span className="text-slate-500 font-bold">➔</span>
                                <span className="px-2.5 py-1 rounded-full bg-slate-900 border border-slate-700 text-indigo-300 font-mono text-[11px] shadow-sm text-center">
                                    {presets.length} / {targetEntities.length || 42} entities
                                </span>
                                <span className="text-slate-500 font-bold">➔</span>
                            </div>

                            {/* Col 3: Destination System */}
                            <div className="w-full">
                                <SearchableSelect
                                    options={targetSystemOptions}
                                    value={targetSystem}
                                    onChange={onChangeTargetSystem}
                                    placeholder="Select destination system..."
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── 2. PRESET SEARCH & TOOLBAR ── */}
                    <div className="relative w-full">
                        <span className="material-symbols-outlined text-slate-400 absolute left-3 top-2.5 text-[17px]">search</span>
                        <input
                            type="text"
                            value={pipelineFilter}
                            onChange={e => setPipelineFilter(e.target.value)}
                            placeholder="Filter pipelines by name, table, or entity..."
                            className="w-full bg-slate-950 border border-slate-750 hover:border-slate-600 rounded-xl pl-9 pr-8 py-2 text-xs text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none shadow-inner"
                        />
                        {pipelineFilter && (
                            <button
                                type="button"
                                onClick={() => setPipelineFilter('')}
                                className="absolute right-3 top-2 text-slate-400 hover:text-white text-sm font-bold"
                                title="Clear filter"
                            >&times;</button>
                        )}
                    </div>

                    {/* ── 3. TIER 2: PRESET ACCORDION LIST ── */}
                    <div className="space-y-3">
                        {filteredPresets.length === 0 ? (
                            <div className="p-8 text-center bg-slate-850/40 rounded-xl border border-dashed border-slate-800 text-slate-400 text-xs">
                                <span className="material-symbols-outlined text-3xl text-slate-500 block mb-2">account_tree</span>
                                {pipelineFilter ? 'No pipelines match your search filter.' : `No pipelines configured in standard set "${setName}".`}
                            </div>
                        ) : (
                            filteredPresets.map(preset => {
                                const presetId = preset.MappingID || preset.TargetQBEntity || preset.EntityType;
                                const isPresetExpanded = expandedPresetId === presetId;

                                return (
                                    <PresetAccordionItem
                                        key={presetId}
                                        preset={preset}
                                        isExpanded={isPresetExpanded}
                                        onToggleExpand={() => onToggleExpandPreset(presetId)}
                                        token={token}
                                        sourceSystem={sourceSystem}
                                        targetSystem={targetSystem}
                                        availableTables={availableTables}
                                        targetEntities={targetEntities}
                                        onSavePreset={onSavePreset}
                                        onDuplicatePreset={onDuplicatePreset}
                                        onRenamePreset={onRenamePreset}
                                        onDeletePreset={onDeletePreset}
                                        onResetPresetDefaults={onResetPresetDefaults}
                                    />
                                );
                            })
                        )}
                    </div>

                    {/* ── 4. ADD NEW PIPELINE BUTTON ── */}
                    <div className="pt-2">
                        <button
                            type="button"
                            onClick={() => onAddNewPipeline(setName)}
                            className="w-full py-2.5 px-4 bg-slate-800/80 hover:bg-slate-800 border border-dashed border-slate-700 hover:border-indigo-500 text-slate-300 hover:text-indigo-300 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm"
                        >
                            <span className="material-symbols-outlined text-[16px]">add_circle</span>
                            Add New Pipeline to {setName}
                        </button>
                    </div>

                </div>
            )}
        </div>
    );
}
